import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

/**
 * Hook for fetching systems associated with a specific customer and site.
 * Used in the AssignAssetDialog for cascading selection (customer + site → system).
 *
 * The getSystemsList query filters by customerId and siteId on the backend.
 *
 * When siteId is null, the query is skipped and an empty array is returned.
 *
 * @param siteId - The site ID to fetch systems for, or null to skip
 * @param customerId - The customer ID to scope systems to
 * @returns Systems list with id and name, plus loading state
 */
export function useSystemsForSite(siteId: string | null, customerId?: string | null): {
  systems: Array<{ id: string; name: string }>
  loading: boolean
} {
  const { data, loading, previousData } = useQueryWithEndpoint<
    gqlTypes.GetSystemsListQuery,
    gqlTypes.GetSystemsListQueryVariables
  >(
    gqlTypes.GetSystemsListDocument,
    {
      variables: {
        siteId: siteId || "",
        customerId: customerId || undefined,
        perPage: 100,
      },
      skip: !siteId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  if (!siteId) {
    return { systems: [], loading: false }
  }

  // Use previousData while loading to prevent visual flicker
  const effectiveData = data ?? previousData

  const systems: Array<{ id: string; name: string }> =
    effectiveData?.systemsList?.data?.map((system) => ({
      id: system.id,
      name: system.name,
    })) ?? []

  return { systems, loading: loading && !effectiveData }
}
